import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { Volume2, VolumeX, Share2, Bookmark, BookmarkCheck, MessageCircle, Twitter, Facebook, Link as LinkIcon } from "lucide-react";

// ---- Read Aloud (Hindi TTS) ----
export function ReadAloudButton({ text }) {
  const [speaking, setSpeaking] = useState(false);
  const utterRef = useRef(null);
  const supported = typeof window !== "undefined" && "speechSynthesis" in window;
  if (!supported) return null;
  const toggle = () => {
    if (speaking) { window.speechSynthesis.cancel(); setSpeaking(false); return; }
    const u = new SpeechSynthesisUtterance(text);
    u.lang = "hi-IN"; u.rate = 0.95; u.pitch = 1;
    u.onend = () => setSpeaking(false);
    u.onerror = () => setSpeaking(false);
    utterRef.current = u;
    window.speechSynthesis.speak(u);
    setSpeaking(true);
  };
  return (
    <button onClick={toggle} className={`inline-flex items-center gap-2 text-xs font-admin uppercase tracking-widest px-3 py-1.5 rounded-full border transition ${
      speaking ? "bg-red-500 text-white border-red-500" : "bg-white border-slate-200 text-[#0F172A] hover:border-[#EA580C]"
    }`} data-testid="read-aloud-btn">
      {speaking ? <VolumeX className="w-3.5 h-3.5"/> : <Volume2 className="w-3.5 h-3.5"/>}
      {speaking ? "बंद करें" : "सुनें"}
    </button>
  );
}

// ---- Share Buttons ----
export function ShareButtons({ url, title }) {
  const enc = encodeURIComponent(url);
  const encT = encodeURIComponent(title);
  const links = [
    { name: "WhatsApp", icon: MessageCircle, href: `https://wa.me/?text=${encT}%20${enc}`, color: "bg-emerald-500 hover:bg-emerald-600" },
    { name: "Facebook", icon: Facebook, href: `https://www.facebook.com/sharer/sharer.php?u=${enc}`, color: "bg-blue-600 hover:bg-blue-700" },
    { name: "Twitter", icon: Twitter, href: `https://twitter.com/intent/tweet?url=${enc}&text=${encT}`, color: "bg-slate-800 hover:bg-slate-900" },
  ];
  const copy = () => { navigator.clipboard.writeText(url); toast.success("लिंक कॉपी हो गया"); };
  return (
    <div className="inline-flex items-center gap-1.5" data-testid="share-buttons">
      <span className="text-xs font-admin uppercase tracking-widest text-slate-500 flex items-center gap-1"><Share2 className="w-3.5 h-3.5"/>शेयर:</span>
      {links.map((l) => (
        <a key={l.name} href={l.href} target="_blank" rel="noopener noreferrer" title={l.name}
          className={`w-8 h-8 rounded-full flex items-center justify-center text-white ${l.color} transition`}
          data-testid={`share-${l.name.toLowerCase()}`}>
          <l.icon className="w-3.5 h-3.5"/>
        </a>
      ))}
      <button onClick={copy} title="Copy Link" className="w-8 h-8 rounded-full flex items-center justify-center text-white bg-slate-500 hover:bg-slate-600" data-testid="share-copy">
        <LinkIcon className="w-3.5 h-3.5"/>
      </button>
    </div>
  );
}

// ---- Bookmark ----
export function BookmarkButton({ slug, title }) {
  const [marked, setMarked] = useState(false);
  useEffect(() => {
    const bm = JSON.parse(localStorage.getItem("in21_bookmarks") || "[]");
    setMarked(bm.some((b) => b.slug === slug));
  }, [slug]);
  const toggle = () => {
    const bm = JSON.parse(localStorage.getItem("in21_bookmarks") || "[]");
    if (marked) {
      const next = bm.filter((b) => b.slug !== slug);
      localStorage.setItem("in21_bookmarks", JSON.stringify(next));
      setMarked(false);
      toast.success("बुकमार्क हटाया");
    } else {
      bm.unshift({ slug, title, at: new Date().toISOString() });
      localStorage.setItem("in21_bookmarks", JSON.stringify(bm.slice(0, 200)));
      setMarked(true);
      toast.success("सहेजा गया");
    }
  };
  return (
    <button onClick={toggle} className={`inline-flex items-center gap-2 text-xs font-admin uppercase tracking-widest px-3 py-1.5 rounded-full border transition ${
      marked ? "bg-amber-500 text-white border-amber-500" : "bg-white border-slate-200 text-[#0F172A] hover:border-amber-500"
    }`} data-testid="bookmark-btn">
      {marked ? <BookmarkCheck className="w-3.5 h-3.5"/> : <Bookmark className="w-3.5 h-3.5"/>}
      {marked ? "सहेजा गया" : "सहेजें"}
    </button>
  );
}

// ---- Related ----
export function RelatedArticles({ slug }) {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get(`/news/${slug}/related`).then((r) => setItems(r.data)).catch(() => {}); }, [slug]);
  if (items.length === 0) return null;
  return (
    <section className="mt-10 pt-8 border-t border-slate-200" data-testid="related-articles">
      <h3 className="font-devnagri-heading text-xl font-black text-[#0F172A] mb-4">इसे भी पढ़ें</h3>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {items.map((n) => (
          <Link key={n.id} to={`/article/${n.slug}`} className="group block bg-white rounded-lg border border-slate-100 overflow-hidden hover:shadow-md transition" data-testid={`related-${n.id}`}>
            {n.image_url && <div className="aspect-video bg-slate-100"><img src={n.image_url} alt="" className="w-full h-full object-cover group-hover:scale-105 transition"/></div>}
            <div className="p-3">
              <div className="text-[10px] uppercase tracking-widest text-[#EA580C] font-bold mb-1">{n.category}</div>
              <div className="font-devnagri-heading font-bold text-sm text-[#0F172A] group-hover:text-[#DC2626] line-clamp-3 leading-snug">{n.title}</div>
            </div>
          </Link>
        ))}
      </div>
    </section>
  );
}

// ---- Poll Widget ----
export function PollWidget({ newsId }) {
  const [poll, setPoll] = useState(null);
  const [voted, setVoted] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!newsId) { setLoading(false); return; }
    api.get(`/news/${newsId}/poll`).then((r) => {
      setPoll(r.data);
      if (r.data) {
        const vk = `in21_voted_${r.data.id}`;
        setVoted(!!localStorage.getItem(vk));
      }
    }).finally(() => setLoading(false));
  }, [newsId]);

  if (loading || !poll) return null;

  const vote = async (idx) => {
    try {
      const voter_id = localStorage.getItem("in21_voter_id") || (() => { const v = crypto.randomUUID(); localStorage.setItem("in21_voter_id", v); return v; })();
      const { data } = await api.post(`/polls/${poll.id}/vote`, { option_index: idx, voter_id });
      setPoll(data); localStorage.setItem(`in21_voted_${poll.id}`, "1"); setVoted(true);
      toast.success("वोट रिकॉर्ड हुआ");
    } catch (e) { toast.error(e.response?.data?.detail || "वोट नहीं हो सका"); }
  };
  const total = poll.total_votes || 0;

  return (
    <section className="my-8 bg-gradient-to-br from-amber-50 to-orange-50 border-2 border-amber-200 rounded-xl p-5" data-testid="poll-widget">
      <div className="uppercase tracking-widest text-[10px] font-bold text-amber-700 mb-2">पाठक पोल · Reader Poll</div>
      <h3 className="font-devnagri-heading font-black text-lg text-[#0F172A] mb-4">{poll.question}</h3>
      <div className="space-y-2">
        {poll.options.map((o, i) => {
          const pct = total ? Math.round(o.votes * 100 / total) : 0;
          return voted ? (
            <div key={`v-${o.text}-${i}`} className="relative rounded-md overflow-hidden border border-amber-200 bg-white">
              <div className="absolute inset-y-0 left-0 bg-amber-200/60" style={{ width: `${pct}%` }}/>
              <div className="relative px-3 py-2 flex justify-between text-sm"><span>{o.text}</span><span className="font-bold text-amber-800">{pct}%</span></div>
            </div>
          ) : (
            <button key={`b-${o.text}-${i}`} onClick={() => vote(i)}
              className="w-full text-left px-3 py-2 rounded-md border border-amber-200 bg-white hover:bg-amber-100 text-sm font-medium text-[#0F172A]"
              data-testid={`poll-opt-${i}`}>
              {o.text}
            </button>
          );
        })}
      </div>
      {voted && <div className="text-[10px] text-amber-700 mt-2">कुल वोट: {total}</div>}
    </section>
  );
}
